import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Info, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateTemplateDocument, useDeleteTemplateDocument } from "@/hooks/useProjects";
import {
  PENDING_SELECTION_STATUS,
  buildEvidenceSelectionDocIds,
  diffSelection,
  hasExtractedData,
} from "@/lib/extractionSelection";
import type { Document, TemplateDocumentData } from "@/types/models";

export interface SelectFromEvidenceBaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The project's Evidence Base (documents added in Step One) — the only documents offered. */
  documents: Document[];
  /** Every active template-document row for the template tab, Pending placeholders included. */
  templateDocuments: TemplateDocumentData[];
  projectId: string;
  templateId: string | undefined;
  /** Blocks saving, e.g. while this tab is extracting. */
  saveDisabledReason?: string;
  /** Shows Step One (Evidence Base) so the user can upload documents. */
  onGoToEvidenceBase: () => void;
  /** Called after a save with the document ids that were added successfully. */
  onSaved?: (addedDocumentIds: string[]) => void;
}

export const SELECT_FROM_EVIDENCE_BASE_TITLE = "Select from Evidence Base";
export const EMPTY_EVIDENCE_BASE_MESSAGE =
  "There are no documents in this project's Evidence Base yet. Return to Step One (Evidence Base) to upload documents first.";
export const MISSING_DOCUMENT_HINT =
  "Can't see a document? Return to Step One (Evidence Base) to upload it, then select it here.";

const SelectFromEvidenceBaseDialog = ({
  open,
  onOpenChange,
  documents,
  templateDocuments,
  projectId,
  templateId,
  saveDisabledReason,
  onGoToEvidenceBase,
  onSaved,
}: SelectFromEvidenceBaseDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [failedAddCount, setFailedAddCount] = useState(0);
  const [failedRemoveCount, setFailedRemoveCount] = useState(0);
  const createTemplateDocument = useCreateTemplateDocument();
  const deleteTemplateDocument = useDeleteTemplateDocument();

  // What is saved as of the moment the dialog opened. Saving diffs the ticks against this, and
  // only successful changes advance it — a failed add/remove stays in the diff so retrying
  // re-attempts just that document.
  const baselineRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      const saved = buildEvidenceSelectionDocIds(templateDocuments);
      baselineRef.current = saved;
      setSelectedIds(documents.filter((d) => saved.has(d.id)).map((d) => d.id));
    } else {
      setSearch("");
      setConfirmingRemoval(false);
      setFailedAddCount(0);
      setFailedRemoveCount(0);
    }
    // Seed only when the dialog opens: a refetch while it's open must not undo the user's ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A document extracted into several subgroups has one row per subgroup — removing it removes them all.
  const rowsByDocumentId = useMemo(() => {
    const map = new Map<string, TemplateDocumentData[]>();
    for (const row of templateDocuments) {
      const rows = map.get(row.documentId) ?? [];
      rows.push(row);
      map.set(row.documentId, rows);
    }
    return map;
  }, [templateDocuments]);

  const { toAdd, toRemove } = diffSelection(baselineRef.current, selectedIds);
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;
  // Removing an extracted document discards its results, not just a placeholder — confirm first.
  const toRemoveExtracted = toRemove.filter((id) => (rowsByDocumentId.get(id) ?? []).some(hasExtractedData));

  const q = search.trim().toLowerCase();
  const filteredDocs = q
    ? documents.filter((d) =>
        [d.title, d.filename, d.author].some((v) => (v ?? "").toLowerCase().includes(q))
      )
    : documents;

  const toggleDoc = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const commitChanges = async () => {
    if (!templateId) return;
    setIsSaving(true);
    setConfirmingRemoval(false);

    // Several mutations run concurrently, so a local flag tracks the save — a shared mutation's
    // own isPending only reflects whichever call settled last.
    const addResults = await Promise.allSettled(
      toAdd.map((documentId) =>
        createTemplateDocument.mutateAsync({
          projectId,
          templateId,
          documentId,
          data: null,
          status: PENDING_SELECTION_STATUS,
        })
      )
    );
    const removeResults = await Promise.allSettled(
      toRemove.map((documentId) =>
        Promise.all(
          (rowsByDocumentId.get(documentId) ?? []).map((row) =>
            deleteTemplateDocument.mutateAsync({ projectId, templateId, documentDataId: row.id })
          )
        )
      )
    );
    setIsSaving(false);

    const nextBaseline = new Set(baselineRef.current);
    const added: string[] = [];
    let failedAdds = 0;
    toAdd.forEach((id, i) => {
      if (addResults[i].status === "fulfilled") {
        nextBaseline.add(id);
        added.push(id);
      } else {
        failedAdds += 1;
      }
    });
    let failedRemoves = 0;
    toRemove.forEach((id, i) => {
      if (removeResults[i].status === "fulfilled") nextBaseline.delete(id);
      else failedRemoves += 1;
    });
    baselineRef.current = nextBaseline;
    setFailedAddCount(failedAdds);
    setFailedRemoveCount(failedRemoves);

    if (added.length > 0) onSaved?.(added);
    if (failedAdds === 0 && failedRemoves === 0) onOpenChange(false);
  };

  const handleSaveClick = () => {
    if (toRemoveExtracted.length > 0 && !confirmingRemoval) {
      setConfirmingRemoval(true);
      return;
    }
    void commitChanges();
  };

  const isEvidenceBaseEmpty = documents.length === 0;
  const isSaveBlocked = !!saveDisabledReason || !templateId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg flex flex-col max-h-[80vh]">
        <DialogHeader className="shrink-0">
          <DialogTitle>{SELECT_FROM_EVIDENCE_BASE_TITLE}</DialogTitle>
          <DialogDescription>
            {confirmingRemoval
              ? "Confirm removal"
              : "Choose which Evidence Base documents to include in this template's data extraction."}
          </DialogDescription>
        </DialogHeader>

        {isEvidenceBaseEmpty ? (
          <Alert variant="info" data-testid="empty-evidence-base">
            <Info className="h-4 w-4" />
            <AlertDescription className="space-y-3">
              <p>{EMPTY_EVIDENCE_BASE_MESSAGE}</p>
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onGoToEvidenceBase}>
                <ArrowLeft className="w-4 h-4" /> Go to Evidence Base
              </Button>
            </AlertDescription>
          </Alert>
        ) : confirmingRemoval ? (
          <div className="space-y-3 py-2" data-testid="confirm-removal">
            <p className="text-sm text-foreground">
              This will permanently delete the extracted data for {toRemoveExtracted.length}{" "}
              {toRemoveExtracted.length === 1 ? "document" : "documents"}:
            </p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground max-h-48 overflow-y-auto">
              {toRemoveExtracted.map((id) => (
                <li key={id}>{documents.find((d) => d.id === id)?.title ?? id}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col min-h-0 flex-1">
            <input
              type="text"
              placeholder="Search documents..."
              aria-label="Search documents"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="shrink-0 w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring mb-2"
            />
            <div className="flex-1 overflow-y-auto min-h-0">
              {filteredDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No documents match your search.</p>
              ) : (
                <ul className="divide-y">
                  {filteredDocs.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex items-start gap-3 py-3 px-1 cursor-pointer"
                      onClick={() => toggleDoc(doc.id)}
                    >
                      <Checkbox
                        checked={selectedIds.includes(doc.id)}
                        onCheckedChange={() => toggleDoc(doc.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={doc.title || doc.filename}
                        className="mt-0.5 shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground leading-snug">{doc.title || doc.filename}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[doc.author, doc.year].filter(Boolean).join(" • ")}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="shrink-0 pt-2 flex items-start justify-between gap-4">
              <p className="text-xs text-muted-foreground">{MISSING_DOCUMENT_HINT}</p>
              <p className="text-xs text-muted-foreground whitespace-nowrap">
                {selectedIds.length} document{selectedIds.length === 1 ? "" : "s"} selected
              </p>
            </div>
          </div>
        )}

        {!confirmingRemoval && (failedAddCount > 0 || failedRemoveCount > 0) && (
          <p className="text-sm text-destructive">
            {failedAddCount > 0 && `Failed to add ${failedAddCount} ${failedAddCount === 1 ? "document" : "documents"}. `}
            {failedRemoveCount > 0 &&
              `Failed to remove ${failedRemoveCount} ${failedRemoveCount === 1 ? "document" : "documents"}. `}
            Try again.
          </p>
        )}

        <DialogFooter className="shrink-0">
          {confirmingRemoval ? (
            <>
              <Button type="button" variant="outline" onClick={() => setConfirmingRemoval(false)}>
                Back
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isSaving || isSaveBlocked}
                onClick={() => void commitChanges()}
                className="gap-1.5"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Remove &amp; Save
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {!isEvidenceBaseEmpty && (
                <Button
                  type="button"
                  variant="brand"
                  disabled={!hasChanges || isSaving || isSaveBlocked}
                  title={saveDisabledReason}
                  onClick={handleSaveClick}
                  className="gap-1.5"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Selection
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectFromEvidenceBaseDialog;
